import { getFixtures, getResults, type FixturesByGameweek, type ResultsByGameweek } from './api/football'
import type { Game } from './types'

/**
 * The client's fixtures/results store.
 *
 * No mock data is imported: the season is **fetched** from our own server once at app boot and
 * held here.
 *
 * The accessors stay synchronous on purpose. Everything downstream — `teamForm.ts`,
 * `useGameweekState`, `computePLTable` — is written against a synchronous constant, so rather
 * than make all of that async the load happens once during app initialisation and the loading gate
 * holds the UI until it lands. Before that the accessors return empty and `isSeasonDataLoaded()`
 * is false.
 *
 * The server reads the same data from `server/football/repository.ts` instead of going through
 * here — it should read its own database, not fetch itself over HTTP.
 */

let games: FixturesByGameweek = {}
let results: ResultsByGameweek = {}
let loaded = false
/** Bumped on every successful load, so React state can depend on "the season changed". */
let version = 0

const SESSION_CACHE_KEY = 'rwbet.seasonData.v1'

export function getGamesByGameweek(): Record<number, Game[]> {
  return games
}

export function getActualResultsByGameweek(): ResultsByGameweek {
  return results
}

export function isSeasonDataLoaded(): boolean {
  return loaded
}

export function seasonDataVersion(): number {
  return version
}

/** Replaces the store. Used by the loader below, by the live poll, and by tests. */
export function setSeasonData(nextGames: FixturesByGameweek, nextResults: ResultsByGameweek): void {
  games = nextGames
  results = nextResults
  loaded = true
  version += 1
}

/** Drops everything back to the pre-load state. Tests use it; the app never needs it. */
export function resetSeasonData(): void {
  games = {}
  results = {}
  loaded = false
  version += 1
}

interface CachedSeason {
  games: FixturesByGameweek
  results: ResultsByGameweek
}

function readSessionCache(): CachedSeason | null {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY)
    return raw ? (JSON.parse(raw) as CachedSeason) : null
  } catch {
    // Private browsing, quota, a corrupted entry — none of it is worth failing a load over.
    return null
  }
}

function writeSessionCache(payload: CachedSeason): void {
  try {
    sessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(payload))
  } catch {
    // Same: a cache that can't be written is a slower reload, not an error.
  }
}

/**
 * Fetches the season once. Called from the app's mount-time load.
 *
 * A cached copy is applied immediately so a reload renders straight away, and the network copy
 * replaces it when it lands. Failures propagate — the caller shows an error with a retry, because
 * an empty season renders as a convincing-looking app with no fixtures, which is worse.
 */
export async function loadSeasonData(): Promise<void> {
  const cached = readSessionCache()
  if (cached && !loaded) setSeasonData(cached.games, cached.results)

  const [nextGames, nextResults] = await Promise.all([getFixtures(), getResults()])
  setSeasonData(nextGames, nextResults)
  writeSessionCache({ games: nextGames, results: nextResults })
}

/** Re-reads results only — what the live poll calls when a fixture reaches full time. */
export async function refreshResults(): Promise<void> {
  const nextResults = await getResults()
  setSeasonData(games, nextResults)
  writeSessionCache({ games, results: nextResults })
}
